import {
  Alert,
  Checkbox,
  cn,
  Fieldset,
  NumberInput,
  OrderLineItem,
  RelativeDateTime,
  SimpleSelect,
} from 'corso-admin-ui';
import { useCurrency } from '~/hooks/useCurrency';
import {
  CrewClaimResolutionMethodEnum,
  crewClaimResolutionMethodEnumName,
  CrewClaimTypeEnum,
  CrewMerchantUi,
  NO_SHOPIFY_PRODUCT_ID,
} from 'corso-types';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { reviewableResolutionMethods } from '~/features/claimReview/ClaimReviewProvider';
import { CrewClaimFormInputValues } from '~/features/orderLookup/claimCreateSchemas';
import { toLowerCase } from '~/utils/casing';
import { ClaimCreateVariantExchange } from './ClaimCreateVariantExchange';

function getEligibilityFootnote(
  lineItem: CrewMerchantUi.CrewOrder['lineItems'][0],
) {
  if (lineItem.automationMadeIneligibleOn) {
    return (
      <span className="text-red-600">
        Automation Made Ineligible{' '}
        <RelativeDateTime dateTime={lineItem.automationMadeIneligibleOn} />
      </span>
    );
  }

  return null;
}

const allowedReturnCreateResolutionMethods = [
  CrewClaimResolutionMethodEnum.refund,
  CrewClaimResolutionMethodEnum.giftCard,
  CrewClaimResolutionMethodEnum.variantExchange,
] satisfies (typeof reviewableResolutionMethods)[number][];

export function CrewClaimFields({
  order,
  claimType,
  canCreateClaim,
}: {
  order: CrewMerchantUi.CrewOrder;
  claimType: CrewClaimTypeEnum;
  canCreateClaim: boolean;
}) {
  const { control, register, setValue, watch, formState } =
    useFormContext<CrewClaimFormInputValues>();

  const { currencyCode } = useCurrency();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'claimLineItems',
  });

  const watchFields = watch('claimLineItems');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFields[index],
  }));

  if (!canCreateClaim) {
    return (
      <Alert
        variant="warning"
        heading="This order has not been fulfilled yet. Claims can only be created for fulfilled orders when using Shopify Return Processing."
      />
    );
  }

  return (
    <>
      <Alert
        variant="info"
        heading="Creating this claim will bypass your settings for final sale, exchanges, and eligibility windows."
      />

      <Fieldset legend="Line Items">
        <ul className="space-y-4">
          {order.lineItems.map((lineItem) => {
            const lineItemFieldIndex = controlledFields.findIndex(
              (field) => field.originalStoreOrderLineItemId === lineItem.id,
            );
            const lineItemField = controlledFields[lineItemFieldIndex];

            const { claimReasons } = lineItem[toLowerCase(claimType)];
            const { claimReasonDetails = [] } =
              claimReasons.find(
                (reason) => reason.id === lineItemField?.reasonId,
              ) ?? {};

            const isDisabled =
              !lineItem.currentQuantity || lineItem.isCorsoProduct;

            const selectedReasonId = watch(
              `claimLineItems.${lineItemFieldIndex}.reasonId`,
            );

            const selectedReasonDetailId = watch(
              `claimLineItems.${lineItemFieldIndex}.reasonDetailId`,
            );

            return (
              <li className="group" key={lineItem.id}>
                <div className="flex items-center gap-2">
                  {/* // TODO a11y label */}
                  <Checkbox
                    disabled={isDisabled}
                    className={cn(isDisabled && 'invisible')}
                    checked={!!lineItemField}
                    onChange={(e) => {
                      if (e.target.checked) {
                        append({
                          originalStoreOrderLineItemId: lineItem.id,
                          quantity: lineItem.currentQuantity,
                          // * default returns to refund, warranty to warranty review
                          requestedResolutionMethodEnum:
                            claimType === CrewClaimTypeEnum.return
                              ? CrewClaimResolutionMethodEnum.refund
                              : CrewClaimResolutionMethodEnum.warrantyReview,
                          /* oxlint-disable-next-line @typescript-eslint/no-non-null-assertion */ // TODO address fix
                          reasonId: claimReasons.at(0)!.id,
                          reasonDetailId: claimReasons
                            .at(0)
                            ?.claimReasonDetails.at(0)?.id,
                        });
                      } else {
                        remove(
                          fields.findIndex(
                            (field) =>
                              field.originalStoreOrderLineItemId ===
                              lineItem.id,
                          ),
                        );
                      }
                    }}
                  />

                  <div className="grow">
                    <OrderLineItem
                      src={lineItem.imgUrl}
                      title={lineItem.name}
                      sku={lineItem.sku}
                      quantity={lineItem.currentQuantity}
                      price={{
                        currencyCode,
                        unitPrice: lineItem.unitPrice,
                        unitTax: lineItem.unitTax,
                      }}
                      options={lineItem.optionsFromPlatform}
                    >
                      <OrderLineItem.Detail>
                        {getEligibilityFootnote(lineItem)}
                      </OrderLineItem.Detail>
                    </OrderLineItem>
                  </div>
                </div>
                {lineItemField && (
                  <div className="space-y-4 pt-2 pb-8 pl-7 group-last-of-type:pb-0">
                    <NumberInput
                      id={`quantity-${lineItem.id}`}
                      label="Quantity"
                      min={1}
                      max={lineItem.currentQuantity}
                      required
                      {...register(
                        `claimLineItems.${lineItemFieldIndex}.quantity`,
                        { valueAsNumber: true },
                      )}
                      error={
                        formState.errors.claimLineItems?.[lineItemFieldIndex]
                          ?.quantity?.message
                      }
                    />
                    <div className="flex flex-col gap-4 md:flex-row md:gap-1">
                      <div className="grow">
                        <Controller
                          control={control}
                          name={`claimLineItems.${lineItemFieldIndex}.reasonId`}
                          render={({ field, fieldState }) => (
                            <SimpleSelect
                              label="Reason"
                              options={claimReasons.map((reason) => ({
                                label: reason.name,
                                value: `${reason.id}`,
                              }))}
                              value={`${field.value}`}
                              onChange={(selected) => {
                                const reasonId = Number.parseInt(selected, 10);
                                const claimReason = claimReasons.find(
                                  ({ id }) => id === reasonId,
                                );
                                // reset reason detail while setting reason
                                setValue(
                                  `claimLineItems.${lineItemFieldIndex}`,
                                  {
                                    ...lineItemField,
                                    reasonId,
                                    reasonDetailId:
                                      claimReason?.claimReasonDetails.at(0)?.id,
                                  } as CrewClaimFormInputValues['claimLineItems'][number],
                                );
                              }}
                              error={fieldState.error?.message}
                            />
                          )}
                        />
                      </div>
                      {claimReasonDetails.length > 0 && (
                        <div className="grow">
                          <Controller
                            control={control}
                            name={`claimLineItems.${lineItemFieldIndex}.reasonDetailId`}
                            render={({ field, fieldState }) => (
                              <SimpleSelect
                                label="Reason Detail"
                                options={claimReasonDetails.map(
                                  (reasonDetail) => ({
                                    label: reasonDetail.name,
                                    value: `${reasonDetail.id}`,
                                  }),
                                )}
                                value={`${field.value}`}
                                onChange={(selected) =>
                                  field.onChange(Number.parseInt(selected, 10))
                                }
                                error={fieldState.error?.message}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    {/* // * only returns can be changed on the requested resolution; i.e. warranties MUST always be warranty review */}
                    {claimType === CrewClaimTypeEnum.return && (
                      <Controller
                        control={control}
                        name={`claimLineItems.${lineItemFieldIndex}.requestedResolutionMethodEnum`}
                        render={({ field, fieldState }) => (
                          <SimpleSelect
                            label="Resolution Method"
                            options={allowedReturnCreateResolutionMethods
                              .filter(
                                // omit variant exchange as an option if the line item has no variant for exchange
                                (resolution) =>
                                  resolution !==
                                    CrewClaimResolutionMethodEnum.variantExchange ||
                                  (lineItem.productIdFromPlatform &&
                                    lineItem.productIdFromPlatform !==
                                      NO_SHOPIFY_PRODUCT_ID),
                              )
                              .map((resolution) => ({
                                label:
                                  crewClaimResolutionMethodEnumName[resolution],
                                value: resolution,
                              }))}
                            value={field.value}
                            onChange={(selected) => {
                              setValue(`claimLineItems.${lineItemFieldIndex}`, {
                                ...lineItemField,
                                requestedResolutionMethodEnum: selected,
                                variantExchangeLineItem: undefined, // intentionally always resetting, so that changing from variant exchange to a refund doesn't keep the variant exchange data
                              } as CrewClaimFormInputValues['claimLineItems'][number]);
                              field.onChange(selected); // redundant, but kept for the other side effects to state
                            }}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                    )}
                    {lineItemField.requestedResolutionMethodEnum ===
                      CrewClaimResolutionMethodEnum.variantExchange && (
                      <Controller
                        control={control}
                        name={`claimLineItems.${lineItemFieldIndex}.variantExchangeLineItem`}
                        render={({ field, fieldState }) => (
                          <>
                            <ClaimCreateVariantExchange
                              reasonId={selectedReasonId}
                              reasonDetailId={selectedReasonDetailId}
                              idFromPlatform={lineItem.productIdFromPlatform}
                              selectedQuantity={lineItemField.quantity}
                              variantIdFromPlatform={
                                lineItem.variantIdFromPlatform
                              }
                              onChange={field.onChange}
                            />
                            {fieldState.error?.message && (
                              <Alert
                                variant="critical"
                                heading={fieldState.error.message}
                              />
                            )}
                          </>
                        )}
                      />
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {formState.errors.claimLineItems?.message && (
          <div className="mt-2">
            <Alert
              variant="critical"
              heading={formState.errors.claimLineItems.message}
            />
          </div>
        )}
      </Fieldset>
    </>
  );
}
